import { inject, InjectionToken } from '@angular/core';

import type { StoreConfig } from '@ngrx/store';
import { createSelector } from '@ngrx/store';

import { LOCALE } from '@recruitee/i18n';
import type { SingleEntityState } from '@recruitee/ngrx';
import { createSingleEntityStore, memoizeSelector } from '@recruitee/ngrx';

import jsonData from '../assets/countries.json';
import type { CountryMap, CountryStoreEntity, ParsedJsonCountry, Country } from '../types';
import { EntityStoreKey } from '../types';

type CountryStoreConfig = StoreConfig<SingleEntityState<CountryStoreEntity>>;

const { reducer, namespace, selectors } =
  createSingleEntityStore<CountryStoreEntity>()(EntityStoreKey);

export const COUNTRY_STORE_CONFIG = new InjectionToken<CountryStoreConfig>('COUNTRY_STORE_CONFIG');

export function countryStoreConfigFactory(): CountryStoreConfig {
  return {
    initialState: {
      loaded: true,
      pending: false,
      failed: false,
      error: null,
      data: {
        countries: jsonData,
        currentLocale: inject(LOCALE, { optional: true }) ?? undefined,
      },
    },
  };
}

const mapLocalizedName = (langCode: string) => (country: ParsedJsonCountry) => {
  if (!country['names'][langCode]) {
    throw new Error(`Unsupported locale: ${langCode}`);
  }
  return <Country>{
    ...country,
    name: country.names[langCode],
  };
};
const reduceToMap = (acc: CountryMap, curr: Country) => ({ ...acc, [curr.code]: curr });

const byLanguage = memoizeSelector((code: string) =>
  createSelector(selectors.data, data =>
    data !== null ? data.countries.map(mapLocalizedName(code)) : null,
  ),
);

const byLanguageAsMap = memoizeSelector((code: string) =>
  createSelector(byLanguage(code), collection =>
    collection !== null ? collection.reduce(reduceToMap, {}) : null,
  ),
);

const asList = createSelector(selectors.data, state => {
  if (state !== null) {
    if (!state.currentLocale) {
      console.warn('LOCALE not injected, state.currentLocale not set');
    }
    return state.currentLocale
      ? state.countries
          .map(mapLocalizedName(state.currentLocale))
          .sort((a: Country, b: Country) =>
            a.name.localeCompare(b.name, state.currentLocale, { sensitivity: 'base' }),
          )
      : [];
  }

  return null;
});

const asMap = createSelector(asList, data => (data !== null ? data.reduce(reduceToMap, {}) : null));

const CountrySelectors = {
  byLanguage,
  byLanguageAsMap,
  asList,
  asMap,
};

export { CountrySelectors, reducer as CountryDataReducer, namespace as CountryDataNamespace };
